import type { Location } from "react-router-dom";
import { Route, Routes, useLocation } from "react-router-dom";
import BottomTabBar from "./components/BottomTabBar";
import ConnectMspBanner from "./components/ConnectMspBanner";
import BillingBanner from "./components/BillingBanner";
import Billing from "./views/Billing";
import Dashboard from "./views/Dashboard";
import GapModal from "./views/GapModal";
import MassModal from "./views/MassModal";
import VolunteerModal from "./views/VolunteerModal";
import VolunteersModal from "./views/VolunteersModal";
import Settings from "./views/Settings";
import Adoption from "./views/Adoption";
import Shortlist from "./views/Shortlist";
import Templates from "./views/Templates";
import HolyWeek from "./views/HolyWeek";
import HolyWeekPrint from "./views/HolyWeekPrint";
import NotFound from "./views/NotFound";
import SignIn from "./views/SignIn";

interface LocationState {
  backgroundLocation?: Location;
}

// Paths that render as a modal overlay on top of a background view.
// Add new modal paths here so the routing layer keeps the background
// rendered while the modal is open.
const MODAL_PATH_PREFIXES = ["/gap/", "/mass/", "/volunteer/", "/volunteers", "/templates"];

function isModalPath(pathname: string): boolean {
  return MODAL_PATH_PREFIXES.some((p) => pathname.startsWith(p));
}

export default function App() {
  const location = useLocation();
  const state = location.state as LocationState | null;
  const isModalRoute = isModalPath(location.pathname);
  // When a modal route is hit directly via URL (no background state),
  // render Dashboard underneath so the modal has visible context.
  const backgroundLocation =
    state?.backgroundLocation ??
    (isModalRoute ? ({ ...location, pathname: "/", search: "", hash: "" } as Location) : null);

  // BottomTabBar is for the in-app shell only. Sign-in renders no
  // chrome at all; the print-roster route is a chrome-less print
  // surface; both opt out.
  const showBottomTabBar =
    !location.pathname.startsWith("/sign-in") &&
    !location.pathname.startsWith("/holy-week/print");

  return (
    <>
      <ConnectMspBanner />
      <BillingBanner />
      <Routes location={backgroundLocation ?? location}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/shortlist" element={<Shortlist />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/adoption" element={<Adoption />} />
        <Route path="/settings/billing" element={<Billing />} />
        <Route path="/holy-week" element={<HolyWeek />} />
        <Route path="/holy-week/print" element={<HolyWeekPrint />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      {isModalRoute ? (
        <Routes>
          <Route path="/gap/:gapId" element={<GapModal />} />
          <Route path="/mass/:massId" element={<MassModal />} />
          <Route path="/volunteer/:volunteerId" element={<VolunteerModal />} />
          <Route path="/volunteers" element={<VolunteersModal />} />
          <Route path="/templates" element={<Templates />} />
        </Routes>
      ) : null}

      {showBottomTabBar ? <BottomTabBar /> : null}
    </>
  );
}
